/**
 * Long press opens a context menu everywhere in this app — except on iOS.
 * Since iOS 13, Safari does not fire `contextmenu` for touch presses at all
 * (https://github.com/mdn/browser-compat-data/issues/6376 — the event only
 * exists there for real pointing devices, e.g. an iPad trackpad's secondary
 * click), so on an iPhone the menus of the chat list, the message list, the
 * gallery and the account sidebar are all unreachable. The documented
 * workaround is a long-press timer, and that is what this is.
 *
 * Watch every touch press and, if the finger rests in place for LONG_PRESS_MS
 * without the browser firing `contextmenu` itself, dispatch one at the
 * finger. Everything downstream is the same code that runs on Android — the
 * `onContextMenu` handlers, their `preventDefault`, the position the menu
 * opens at, and the account sidebar's reorder gesture putting the profile
 * back down when the menu takes over.
 *
 * Whether the browser needs the help is decided per press, not per platform:
 * no user-agent sniffing. A browser that fires the event itself (Blink, at
 * ~500ms — before our timer) simply cancels the fallback for that press.
 */

/** Longer than the ~500ms after which Blink fires its own `contextmenu`. */
const LONG_PRESS_MS = 700
/** Movement below this is the finger being imprecise, not a scroll. */
const SLOP_PX = 8

export function installLongPressContextMenu() {
  let press: {
    x: number
    y: number
    timer: ReturnType<typeof setTimeout>
  } | null = null
  let synthesized = false

  const cancel = () => {
    if (press) {
      clearTimeout(press.timer)
      press = null
    }
  }

  document.addEventListener(
    'touchstart',
    event => {
      cancel()
      synthesized = false

      const touch = event.touches[0]
      const target = event.target as HTMLElement | null
      if (event.touches.length !== 1 || !touch || !target) {
        return
      }
      // A long press on editable text means text editing (cursor placement,
      // the paste callout) — same reason the app's own handlers leave inputs
      // alone. And a press inside an open context menu is aimed at one of its
      // items; a second menu on top would close the one being used.
      if (
        target.closest?.(
          'input, textarea, [contenteditable="true"], .dc-context-menu-layer'
        )
      ) {
        return
      }
      // Explicitly selectable text — message bodies — is the other place the
      // platform's long press has a job of its own: starting a text
      // selection. A menu on top of it would make selecting text on iOS
      // impossible, so those presses are left alone; a message's menu opens
      // from the bubble's non-text parts instead. `user-select: auto`
      // resolves through the parent, so walk up until something decides.
      for (let el: Element | null = target; el; el = el.parentElement) {
        const style = window.getComputedStyle(el)
        const userSelect = style.userSelect || style.webkitUserSelect
        if (userSelect === 'text') {
          return
        }
        if (userSelect === 'none') {
          break
        }
      }

      const { clientX: x, clientY: y } = touch
      press = {
        x,
        y,
        timer: setTimeout(() => {
          press = null
          synthesized = true
          target.dispatchEvent(
            new MouseEvent('contextmenu', {
              bubbles: true,
              cancelable: true,
              clientX: x,
              clientY: y,
            })
          )
        }, LONG_PRESS_MS),
      }
    },
    { passive: true }
  )

  document.addEventListener(
    'touchmove',
    event => {
      const touch = event.touches[0]
      if (!press || !touch) {
        return
      }
      if (
        Math.abs(touch.clientX - press.x) > SLOP_PX ||
        Math.abs(touch.clientY - press.y) > SLOP_PX
      ) {
        cancel()
      }
    },
    { passive: true }
  )

  document.addEventListener(
    'touchend',
    event => {
      cancel()
      if (synthesized) {
        synthesized = false
        // iOS still delivers the press's `click` after the finger lifts (it
        // has no long-press cutoff for it once nothing native consumed the
        // gesture, unlike Android) — and that click would land on the
        // just-opened menu's backdrop and close it. Cancelling the touchend
        // stops the click from being generated at all.
        event.preventDefault()
      }
    },
    // Non-passive for the preventDefault above.
    { passive: false }
  )
  document.addEventListener(
    'touchcancel',
    () => {
      cancel()
      synthesized = false
    },
    { passive: true }
  )

  document.addEventListener(
    'contextmenu',
    event => {
      if (!event.isTrusted) {
        // Ours, on its way to the app's handlers.
        return
      }
      if (synthesized) {
        // The browser's own, arriving after ours on a device whose long
        // press is slower than our timer: the menu is already open, and
        // letting this one through would close it again.
        event.preventDefault()
        event.stopPropagation()
        return
      }
      // The browser handles this press by itself — stay out of its way.
      cancel()
    },
    true
  )
}
